import type {
  Analytics,
  CatalogItem,
  Product,
  ProductLikeResponse,
  ProductListResponse,
  Seller,
  SellerSignupPayload,
  SellerSignupResponse,
  SiteSettings,
  TokenResponse,
  User
} from "./types";

const API_URL = (import.meta.env.VITE_API_URL ?? "https://revlane.onrender.com/api").replace(/\/$/, "");
const API_ORIGIN = API_URL.replace(/\/api\/?$/, "");

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: unknown
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit & { token?: string | null }): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!(init?.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (init?.token) {
    headers.set("Authorization", `Bearer ${init.token}`);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers
  });
  const body = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(`Request failed with status ${response.status}`, response.status, body);
  }
  return body as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<TokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password })
    }),
  sellerSignup: (payload: SellerSignupPayload) =>
    request<SellerSignupResponse>("/auth/seller-signup", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  me: (token: string) => request<User>("/auth/me", { token }),
  withdrawSellerApplication: (token: string) =>
    request<void>("/auth/seller-application", { method: "DELETE", token }),
  products: (query = "") => request<ProductListResponse>(`/products${query}`),
  product: (slug: string) => request<Product>(`/products/${slug}`),
  updateProductLike: (productId: string, liked: boolean) =>
    request<ProductLikeResponse>(`/products/${productId}/likes`, {
      method: "POST",
      body: JSON.stringify({ liked })
    }),
  search: (query: string, filters = "") =>
    request<ProductListResponse>(`/search?q=${encodeURIComponent(query)}${filters}`),
  brands: () => request<CatalogItem[]>("/catalog/brands"),
  vehicleBrands: () => request<CatalogItem[]>("/catalog/vehicle-brands"),
  scales: () => request<CatalogItem[]>("/catalog/scales"),
  tags: () => request<CatalogItem[]>("/catalog/tags"),
  whatsapp: (items: { product_id: string; name: string; quantity: number; price: number }[]) =>
    request<{ message: string; url: string; subtotal: string }>("/checkout/whatsapp-message", {
      method: "POST",
      body: JSON.stringify({ items })
    }),
  sellerProducts: (token: string) => request<Product[]>("/seller/products", { token }),
  createSellerProduct: (token: string, payload: unknown) =>
    request<Product>("/seller/products", { method: "POST", token, body: JSON.stringify(payload) }),
  updateSellerProduct: (token: string, productId: string, payload: unknown) =>
    request<Product>(`/seller/products/${productId}`, { method: "PATCH", token, body: JSON.stringify(payload) }),
  deleteSellerProduct: (token: string, productId: string) =>
    request<void>(`/seller/products/${productId}`, { method: "DELETE", token }),
  uploadSellerProductImages: (token: string, productId: string, files: File[] | FileList) => {
    const body = new FormData();
    Array.from(files).forEach((file) => body.append("files", file));
    return request<Product>(`/seller/products/${productId}/images`, { method: "POST", token, body });
  },
  deleteSellerProductImage: (token: string, productId: string, imageId: string) =>
    request<Product>(`/seller/products/${productId}/images/${imageId}`, { method: "DELETE", token }),
  reorderSellerProductImages: (token: string, productId: string, imageIds: string[]) =>
    request<Product>(`/seller/products/${productId}/images/order`, {
      method: "PATCH",
      token,
      body: JSON.stringify({ image_ids: imageIds })
    }),
  adminAnalytics: (token: string) => request<Analytics>("/admin/analytics", { token }),
  adminSellers: (token: string) => request<Seller[]>("/admin/sellers", { token }),
  adminProducts: (token: string) => request<Product[]>("/admin/products", { token }),
  adminSettings: (token: string) => request<SiteSettings>("/admin/settings", { token }),
  createBrand: (token: string, payload: { name: string; slug: string }) =>
    request<CatalogItem>("/admin/brands", { method: "POST", token, body: JSON.stringify(payload) }),
  createVehicleBrand: (token: string, payload: { name: string; slug: string }) =>
    request<CatalogItem>("/admin/vehicle-brands", { method: "POST", token, body: JSON.stringify(payload) }),
  createScale: (token: string, payload: { name: string; slug: string }) =>
    request<CatalogItem>("/admin/scales", { method: "POST", token, body: JSON.stringify(payload) }),
  createTag: (token: string, payload: { name: string; slug: string }) =>
    request<CatalogItem>("/admin/tags", { method: "POST", token, body: JSON.stringify(payload) }),
  updateAdminSettings: (token: string, payload: Partial<SiteSettings>) =>
    request<SiteSettings>("/admin/settings", { method: "PATCH", token, body: JSON.stringify(payload) }),
  updateSellerApproval: (token: string, sellerId: string, seller_status: Seller["seller_status"]) =>
    request<Seller>(`/admin/sellers/${sellerId}/approval`, {
      method: "PATCH",
      token,
      body: JSON.stringify({ seller_status })
    }),
  deletePendingSeller: (token: string, sellerId: string) =>
    request<void>(`/admin/sellers/${sellerId}`, { method: "DELETE", token })
};

export function imageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("/media/")) return `${API_ORIGIN}${url}`;
  return url;
}
